import { useState, useEffect } from "react";
import { useDarkMode } from "../contexts/DarkModeContext";
import {
  getVisibleNavLinks,
  isSectionEnabled,
  landingPageContent,
  resolveIconComponent,
  resolveImageSource,
} from "../../content/landingPageContent";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] =
    useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { site, navbar } = landingPageContent;
  const navLinks = getVisibleNavLinks();
  const ThemeIcon = resolveIconComponent(
    isDarkMode
      ? navbar.darkModeEnabledIcon
      : navbar.darkModeDisabledIcon,
  );
  const MobileMenuIcon = resolveIconComponent(
    isMobileMenuOpen
      ? navbar.mobileMenuOpenIcon
      : navbar.mobileMenuClosedIcon,
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition =
        element.getBoundingClientRect().top +
        window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white dark:bg-[#100f14] shadow-md"
          : "bg-white dark:bg-[#100f14]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() =>
                window.scrollTo({ top: 0, behavior: "smooth" })
              }
              className="block"
              aria-label={`${site.name} home`}
            >
              <img
                src={resolveImageSource(navbar.logoLightSrc)}
                alt={site.name}
                width={320}
                height={320}
                decoding="async"
                className="h-42 w-auto block dark:hidden"
              />
              <img
                src={resolveImageSource(navbar.logoDarkSrc)}
                alt={site.name}
                width={320}
                height={320}
                decoding="async"
                className="h-42 w-auto hidden dark:block"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.targetId}
                onClick={() => scrollToSection(link.targetId)}
                className="text-[#111111] dark:text-white hover:text-[#B08A2E] dark:hover:text-[#B08A2E] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right side: Dark mode toggle + CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md text-[#111111] dark:text-white hover:bg-gray-100 dark:hover:bg-[#1f1d24] transition-colors"
              aria-label={navbar.themeToggleLabel}
            >
              <ThemeIcon size={20} />
            </button>

            {/* CTA Button */}
            {isSectionEnabled(navbar.primaryCta.sectionKey) && (
              <button
                onClick={() =>
                  scrollToSection(navbar.primaryCta.targetId)
                }
                className="bg-[#8A651E] text-white px-6 py-2.5 rounded-md hover:bg-[#6F5319] transition-all duration-300 hover:shadow-lg hover:shadow-[#8A651E]/30"
              >
                {navbar.primaryCta.label}
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Dark Mode Toggle for Mobile */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md text-[#111111] dark:text-white hover:bg-gray-100 dark:hover:bg-[#1f1d24] transition-colors"
              aria-label={navbar.themeToggleLabel}
            >
              <ThemeIcon size={20} />
            </button>

            <button
              onClick={() =>
                setIsMobileMenuOpen(!isMobileMenuOpen)
              }
              className="text-[#111111] dark:text-white"
              aria-label={
                isMobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              <MobileMenuIcon size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          id="mobile-navigation"
          className="md:hidden bg-white dark:bg-[#100f14] border-t border-gray-200 dark:border-gray-800"
        >
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.targetId}
                onClick={() => scrollToSection(link.targetId)}
                className="block w-full text-left text-[#111111] dark:text-white hover:text-[#B08A2E] dark:hover:text-[#B08A2E] py-2"
              >
                {link.label}
              </button>
            ))}
            {isSectionEnabled(navbar.primaryCta.sectionKey) && (
              <button
                onClick={() =>
                  scrollToSection(navbar.primaryCta.targetId)
                }
                className="w-full bg-[#8A651E] text-white px-6 py-2.5 rounded-md hover:bg-[#6F5319] transition-all"
              >
                {navbar.primaryCta.label}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
